// ============================================================
// AI Literacy Bootcamp — "Build-a-Bot" Craft Edition
// Redesigned for 4 groups of 3 students: cardboard box, A3 paper,
// markers, scissors, tape. No coding, no screens.
// ============================================================

export const AI_DAY_COLORS = [
  'linear-gradient(135deg, #118AB2, #06B6D4)',
  'linear-gradient(135deg, #06D6A0, #059669)',
  'linear-gradient(135deg, #EF4444, #F97316)',
  'linear-gradient(135deg, #A855F7, #6366F1)',
  'linear-gradient(135deg, #F59E0B, #EF4444)',
];

export const AI_DAYS = [
  // ─── DAY 1 ───────────────────────────────────────────────
  {
    day: 1,
    weekday: { en: 'Monday', ja: '月曜日' },
    theme: { en: 'What Is AI? — Build Your Companion', ja: 'AIとは何か？——相棒を作ろう' },
    goal: {
      en: 'Each group builds a cardboard "AI Companion" and can explain the 3 steps (Data → Patterns → Predictions) using their own creature as the example.',
      ja: '各グループが段ボールの「AIコンパニオン」を作り、自分たちの創造物を例に3ステップ（データ→パターン→予測）を説明できるようになる。',
    },
    vocab: [
      { word: { en: 'ARTIFICIAL INTELLIGENCE', ja: '人工知能' }, def: { en: 'A program that learns from examples to make predictions.', ja: '例から学んで予測を行うプログラム' }, emoji: '🤖' },
      { word: { en: 'DATA', ja: 'データ' }, def: { en: 'Information — examples, photos, words, numbers — that AI learns from.', ja: 'AIが学ぶための情報（例・写真・言葉・数字など）' }, emoji: '📊' },
      { word: { en: 'PATTERN', ja: 'パターン' }, def: { en: 'A repeating rule or structure that AI finds inside data.', ja: 'AIがデータの中に見つける繰り返しのルールや構造' }, emoji: '🔁' },
      { word: { en: 'PREDICTION', ja: '予測' }, def: { en: 'AI\'s best guess about something new, based on patterns it learned.', ja: '学んだパターンをもとに、AIが新しいことについて行う最善の予測' }, emoji: '🎯' },
      { word: { en: 'TOOL', ja: 'ツール' }, def: { en: 'Something humans use to do a job — AI is a powerful tool, not a person.', ja: '人間が仕事をするために使うもの——AIは強力なツールであり、人間ではない' }, emoji: '🔧' },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Name Your Creature', ja: '相棒に名前をつけよう' },
          type: 'activity',
          emoji: '📦',
          image: '/assets/ai-day1-creature-diagram.png',
          content: {
            en: `MATERIALS (per group of 3): 1 cardboard box, markers, scissors, tape, googly eyes/stickers (optional).\n\nEach group of 3 decorates the outside of their box into a creature face — eyes, mouth, ears, anything. Give it a team name. Cut a slot in the top ("its mouth") — cards will be fed through it all week.\n\nSAY THIS: "Right now your creature knows nothing. That's true for real AI too — before it's taught anything, it's just an empty box. This week we teach it."\n\n💡 STAFF TIP: Keep it messy and fun — there is no "correct" creature design.`,
            ja: `必要なもの（3人グループごと）：段ボール箱1つ・マーカー・ハサミ・テープ・目玉シール等（あれば）。\n\n3人グループで箱の外側をデコレーションし、生き物の顔にする——目・口・耳、何でもOK。チーム名をつける。上部に投入口（「口」）を切る——今週はここにカードを入れていく。\n\n先生が言うこと：「今、あなたたちの相棒は何も知りません。本物のAIも同じ——何かを教わる前はただの空っぽの箱です。今週、一緒に教えていきましょう。」\n\n💡 スタッフへのアドバイス：「正解」のデザインはありません。自由に楽しく作りましょう。`,
          },
        },
        {
          time: '10:45 – 11:15',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Feed the Creature — Sorting Game', ja: '相棒にエサをあげよう——分類ゲーム' },
          type: 'game',
          emoji: '🍽️',
          content: {
            en: `Teacher shows one picture card at a time (face unlock, YouTube suggestions, light switch, calculator, doorbell, spam filter, alarm clock...). Groups discuss for 10 seconds. If it's AI, one member "feeds" the card into their creature's mouth slot. If it's NOT AI, drop it on the table outside the box.\n\nReveal the answer each round — groups self-check by opening the box at the end.\n\n14 CARDS TO SORT:\nAI: Face unlock, YouTube recommendations, keyboard prediction, spam filter, Google Maps routing, photo face sorting, music suggestions, autocorrect\nNOT AI: Light switch, alarm clock, vending machine, doorbell, calculator, TV remote\n\n💡 STAFF TIP: Let each group argue it out together before feeding the card — the discussion is the learning.`,
            ja: `先生が絵カードを1枚ずつ見せる（顔認証・YouTubeのおすすめ・電灯スイッチ・電卓・ドアベル・迷惑メールフィルター・目覚まし時計など）。グループは10秒間相談する。AIなら1人が相棒の口にカードを入れる。AIじゃなければ箱の外のテーブルに置く。\n\n毎回答えを発表——最後に箱を開けてグループ自身で確認する。\n\n分類する14枚のカード：\nAI：顔認証・YouTubeのおすすめ・キーボード予測・迷惑メールフィルター・グーグルマップのルート案内・写真の顔分類・音楽のおすすめ・自動修正\nAIじゃない：電灯スイッチ・目覚まし時計・自動販売機・ドアベル・電卓・テレビのリモコン\n\n💡 スタッフへのアドバイス：カードを入れる前にグループで話し合わせましょう——その話し合いこそが学びです。`,
          },
        },
        {
          time: '11:15 – 12:00',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'A3 Poster — The 3 Steps', ja: 'A3ポスター——3ステップ' },
          type: 'activity',
          emoji: '🎨',
          image: '/assets/ai-day1-poster-diagram.png',
          content: {
            en: `MATERIALS: 1 sheet A3 colored paper per group, markers/crayons.\n\nEach group draws 3 big comic-strip panels: DATA → PATTERN → PREDICTION, using their own simple drawing for each step (e.g., a pile of photos → a magnifying glass → a lightbulb). Tape the finished poster to the side of their box.\n\nEach group explains their 3 panels to the class in one sentence per panel.\n\n💡 STAFF TIP: Drawing quality doesn't matter — stick figures are perfect. The goal is that every group can point at their own poster and say the 3 words in order.`,
            ja: `必要なもの：グループごとにA3の色画用紙1枚・マーカー/クレヨン。\n\n各グループが3コマの大きな漫画パネルを描く：データ→パターン→予測。各ステップを自分たちの簡単な絵で表現する（例：写真の山→虫眼鏡→電球）。完成したポスターを箱の横にテープで貼る。\n\n各グループが1コマにつき1文でクラスに説明する。\n\n💡 スタッフへのアドバイス：絵の上手さは関係ありません。棒人間で十分。目的は各グループが自分のポスターを指しながら3つの言葉を順番に言えることです。`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 14:15',
          duration: { en: '45 min', ja: '45分' },
          title: { en: '4 Corners — AI or Not?', ja: '4つの角——AIかそうじゃないか？' },
          type: 'game',
          emoji: '🏃',
          content: {
            en: `Label 4 corners of the room: "AI", "NOT AI", "Not Sure", "Ask a Friend." Teacher calls out an everyday item; every student (whole class, mixing all groups) runs to a corner. 2–3 kids explain why they picked it. Repeat 10 rounds.\n\nThis is pure physical energy — no writing, no sitting.\n\n10 ITEMS TO CALL OUT (get faster round by round):\n1. A calculator → NOT AI\n2. Face unlock on a phone → AI\n3. A regular alarm clock → NOT AI\n4. YouTube recommending your next video → AI\n5. A doorbell → NOT AI\n6. Autocorrect on a keyboard → AI\n7. A traffic light on a fixed timer → NOT AI\n8. A self-driving car → AI\n9. A thermostat that just holds one set temperature → NOT AI\n10. A voice assistant like Siri or Alexa → AI\n\n💡 STAFF TIP: Speed it up round by round. By round 8–10 it should feel like a game show. Rounds 7 and 9 are deliberately tricky — good for the "Not Sure" corner and discussion.`,
            ja: `教室の4つの角にラベルを貼る：「AI」「AIじゃない」「わからない」「友達に聞く」。先生が身近なものを1つ言う。全員（グループ混合）が自分の角に走る。2〜3人がなぜその角を選んだか説明する。10ラウンド繰り返す。\n\nこれは純粋な運動の時間——書くことも座ることもなし。\n\n10ラウンドで言うアイテム（だんだん速く）：\n1. 電卓 → AIじゃない\n2. スマホの顔認証ロック → AI\n3. 普通の目覚まし時計 → AIじゃない\n4. YouTubeが次の動画をおすすめする → AI\n5. ドアベル → AIじゃない\n6. キーボードの自動修正 → AI\n7. 決まった時間で切り替わる信号機 → AIじゃない\n8. 自動運転車 → AI\n9. 一定の温度を保つだけのサーモスタット → AIじゃない\n10. SiriやAlexaのような音声アシスタント → AI\n\n💡 スタッフへのアドバイス：ラウンドごとにスピードを上げていきましょう。8〜10ラウンド目にはゲームショーのような盛り上がりに。7と9はあえて難しくしています——「わからない」の角や議論に使いましょう。`,
          },
        },
        {
          time: '14:15 – 14:45',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Gallery Walk & Vote', ja: 'ギャラリーウォーク＆投票' },
          type: 'share',
          emoji: '🖼️',
          content: {
            en: `Groups leave their creature + poster on their table and walk around to see all 4. Each student gets one sticky dot to vote for "most creative creature." Count dots together — cheer for the winner, then remind everyone every creature moves on to Day 2.`,
            ja: `各グループは相棒とポスターを机に置いたまま、教室を回って他のグループの作品を見る。各生徒はシールを1枚持ち、「最も創造的な相棒」に投票する。みんなでシールを数えて優勝チームを称える——そして全ての相棒が2日目に進むことを伝える。`,
          },
        },
        {
          time: '14:45 – 15:15',
          duration: { en: '30 min', ja: '30分' },
          title: { en: '5-Question Relay Quiz', ja: '5問リレークイズ' },
          type: 'game',
          emoji: '🏁',
          content: {
            en: `Tape 5 questions on the wall in different spots. On "go," one runner from each group races to a question, reads it, runs back and tells teammates. Team agrees on an answer and shouts it. First correct shout scores a point. Rotate runners each question.\n\n5 QUESTIONS:\n1. What does AI stand for? → Artificial Intelligence\n2. AI learns from ___ (one word) → DATA\n3. What are AI's 3 steps? → Data → Patterns → Predictions\n4. Is a calculator AI? → NO — never learns\n5. Is face unlock AI? → YES — learned from many faces`,
            ja: `教室の壁の異なる場所に5つの質問を貼る。「よーい、どん」で各グループから1人が質問まで走り、読んで、戻ってチームに伝える。チームで答えを相談して叫ぶ。最初に正解を叫んだチームが得点。質問ごとに走者を交代。\n\n5つの質問：\n1. AIは何の略？→人工知能\n2. AIは___から学ぶ（1語）→データ\n3. AIの3ステップは？→データ→パターン→予測\n4. 電卓はAI？→NO——決して学ばない\n5. 顔認証はAI？→YES——たくさんの顔から学んだ`,
          },
        },
        {
          time: '15:15 – 16:00',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Wrap-Up Circle', ja: '振り返りサークル' },
          type: 'share',
          emoji: '🔄',
          content: {
            en: `Sit in a circle with all 4 creatures visible. Each group says one sentence in their own words: "AI is a machine that ___." Write each sentence on a card and keep it — read it back on Friday to see how it's grown.\n\nClose: "Tomorrow we feed our creatures real training data — and watch them make guesses!"`,
            ja: `4体の相棒が見える円になって座る。各グループが自分たちの言葉で一文言う：「AIは___する機械です。」その文をカードに書いて保管——金曜日に読み返してどう成長したか確認する。\n\n締め：「明日は相棒に本物の訓練データを与えて、予測させてみます！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'ai1-detector',
        emoji: '🤖',
        title: { en: 'My AI Companion — Build Log', ja: '私のAIコンパニオン——製作記録' },
        fields: [
          { type: 'header', label: { en: 'The 3 Steps of AI', ja: 'AIの3ステップ' } },
          { type: 'line', label: { en: 'Step 1: ___', ja: 'ステップ1：___' } },
          { type: 'line', label: { en: 'Step 2: ___', ja: 'ステップ2：___' } },
          { type: 'line', label: { en: 'Step 3: ___', ja: 'ステップ3：___' } },
          { type: 'header', label: { en: 'Our Creature', ja: '私たちの相棒' } },
          { type: 'line', label: { en: 'Creature name:', ja: '相棒の名前：' } },
          { type: 'draw', label: { en: 'Draw your creature here:', ja: '相棒の絵を描こう：' } },
          { type: 'multiline', label: { en: 'Today I learned that AI is:', ja: '今日学んだAIとは：' } },
        ],
      },
    ],
  },

  // ─── DAY 2 ───────────────────────────────────────────────
  {
    day: 2,
    weekday: { en: 'Tuesday', ja: '火曜日' },
    theme: { en: 'How AI Learns — Feed Your Creature', ja: 'AIの学び方——相棒にエサをあげよう' },
    goal: {
      en: 'Groups physically "train" their creature with data cards and can explain what happens when it meets something new.',
      ja: 'グループがデータカードで相棒を実際に「訓練」し、新しいものに出会ったときに何が起きるか説明できるようになる。',
    },
    vocab: [
      { word: { en: 'TRAINING DATA', ja: '訓練データ' }, def: { en: 'The examples given to AI so it can learn patterns.', ja: 'AIがパターンを学ぶために与えられる例' }, emoji: '📚' },
      { word: { en: 'MACHINE LEARNING', ja: '機械学習' }, def: { en: 'AI that improves automatically by finding patterns in data.', ja: 'データのパターンを見つけることで自動的に改善するAI' }, emoji: '🧠' },
      { word: { en: 'LABEL', ja: 'ラベル' }, def: { en: 'The correct answer given to AI during training so it knows what to predict.', ja: '訓練中にAIに与えられる正解——何を予測すべきかを教える' }, emoji: '🏷️' },
      { word: { en: 'ACCURATE', ja: '正確' }, def: { en: 'Getting the right answer most of the time — AI becomes more accurate with more data.', ja: '多くの場合で正しい答えを出すこと——データが多いほどAIはより正確になる' }, emoji: '🎯' },
      { word: { en: 'ALGORITHM', ja: 'アルゴリズム' }, def: { en: 'A set of rules that tells AI how to find patterns and make decisions.', ja: 'AIがパターンを見つけ判断するための一連のルール' }, emoji: '⚙️' },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Recap — Meet the Creatures Again', ja: '振り返り——相棒に再会しよう' },
          type: 'lesson',
          emoji: '🔄',
          content: {
            en: `Each group holds up their creature. Teacher holds up 3 cards reused from yesterday's Day 1 sorting-card set — groups shout AI/NOT AI together.\n\nUSE THESE 3 CARDS (pull from your printed Day 1 set, no new printing needed):\n1. Face Unlock → AI\n2. Calculator → NOT AI\n3. YouTube Recommendations → AI\n\nSAY THIS: "Yesterday your creature started learning — it ate its first examples. Today we feed it a LOT more, and watch it get smarter the more examples it eats."`,
            ja: `各グループが相棒を持ち上げる。先生が昨日のDay1分類カードから3枚を再利用して見せる——グループ全員でAI/AIじゃないを叫ぶ。\n\n使う3枚のカード（Day1で印刷したセットから使う、新しい印刷は不要）：\n1. 顔認証 → AI\n2. 電卓 → AIじゃない\n3. YouTubeのおすすめ → AI\n\n先生が言うこと：「昨日、相棒は学び始めました——最初の例を食べましたね。今日はもっとたくさん食べさせて、食べれば食べるほど賢くなるのを見てみましょう。」`,
          },
        },
        {
          time: '10:45 – 11:30',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Feed the Creature — Training Data Cards', ja: '相棒にエサをあげよう——訓練データカード' },
          type: 'activity',
          emoji: '📦',
          image: '/assets/ai-day2-data-cards-preview.png',
          content: {
            en: `MATERIALS: 12 small paper "data cards" per group (half one category like dogs, half a mix of other animals).\n\nGroups sort and physically feed only the "dog" cards into the creature's mouth, saying out loud what makes each one a dog. Then show 2 "mystery" cards (a wolf, a fox) — group predicts "would my creature call this a dog?" and explains why.\n\nThis IS training data → pattern → prediction, made physical.\n\n💡 STAFF TIP: Let groups be wrong about the mystery cards — the "why" discussion after is the real lesson.`,
            ja: `必要なもの：グループごとに小さな紙の「データカード」12枚（半分は犬などの1カテゴリー、半分は他の動物いろいろ）。\n\nグループは「犬」カードだけを相棒の口に入れながら、それぞれ何が犬らしいか声に出して言う。次に「謎の」カードを2枚見せる（オオカミ・キツネ）——グループは「相棒はこれを犬と言うかな？」と予測し理由を説明する。\n\nこれがまさに訓練データ→パターン→予測を実際に体験することです。\n\n💡 スタッフへのアドバイス：謎のカードで間違えてもOK——その後の「なぜ」の議論こそが本当の学びです。`,
          },
        },
        {
          time: '11:30 – 12:00',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Human Robot Game', ja: '人間ロボットゲーム' },
          type: 'game',
          emoji: '🤖',
          content: {
            en: `One student per group becomes "the robot" and can only follow rules given by the teacher — no thinking allowed.\n\nSTEP 1: Give one rule (e.g., "if I clap once, wave").\nSTEP 2: Add a second rule ("if I clap twice, spin").\nSTEP 3: Give an unclapped signal (stomp) — robot freezes, confused.\n\nDiscuss: "What just happened? What should AI do when it sees something it was never trained on?"\n\nSwap robots so every group gets a turn.`,
            ja: `各グループから1人が「ロボット」役になり、先生が出すルールにしか従えない——考えることは禁止。\n\nステップ1：ルールを1つ教える（例：「1回拍手したら手を振る」）。\nステップ2：2つ目のルールを追加（「2回拍手したら回る」）。\nステップ3：拍手じゃない合図を出す（足踏み）——ロボットは混乱してフリーズ。\n\n議論：「何が起きた？学んだことのないものを見たとき、AIはどうすればいい？」\n\n全グループが体験できるようロボット役を交代する。`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 14:15',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'A3 Comic — My Creature\'s Training Day', ja: 'A3漫画——相棒の訓練日記' },
          type: 'activity',
          emoji: '✏️',
          image: '/assets/ai-day2-comic-diagram.png',
          content: {
            en: `Groups draw a 4-panel comic on A3 paper showing their creature being fed data, finding a pattern, and making a guess (right or wrong!). Tape the comic to the box.\n\nEach group shares their comic in one sentence per panel.`,
            ja: `グループはA3用紙に4コマ漫画を描き、相棒がデータを食べて、パターンを見つけて、予測する（正解でも不正解でも！）様子を表現する。漫画を箱にテープで貼る。\n\n各グループが1コマにつき1文で漫画を発表する。`,
          },
        },
        {
          time: '14:15 – 14:45',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Pattern Relay — Physical', ja: 'パターンリレー——身体を使って' },
          type: 'game',
          emoji: '🏁',
          content: {
            en: `MATERIALS: colored paper shapes (circles/squares/triangles in red, blue, yellow, green), cut ahead of time — see the printable shape sheet. Tape each sequence on the floor/wall with a blank space at the end. Pile extra mixed shapes at the starting line for runners to choose from.\n\nGroups race in relay: one runner at a time runs up, picks the correct next shape from the pile, places it in the blank, then tags the next runner.\n\n5 SEQUENCES (easy to hard):\n1. 🔴🔵🔴 🔴🔵🔴 🔴🔵__ → 🔴\n2. 🟡🔵🔵 🟡🔵🔵 🟡🔵__ → 🔵\n3. 🟢🟢🔴 🟢🟢🔴 🟢🟢__ → 🔴\n4. 🔺🔺🟦🟦 🔺🔺🟦🟦 🔺🔺__ → 🟦\n5. AI WORDS (use word cards, not shapes): "DATA → ___ → PREDICTIONS" → PATTERNS`,
            ja: `必要なもの：色画用紙で作った形（赤・青・黄・緑の丸・四角・三角）を事前に切っておく——印刷用シート参照。各数列を床や壁にテープで貼り、最後を空欄にする。スタートラインに色々な形を混ぜて山にしておき、走者が選べるようにする。\n\nグループはリレー形式で競争：1人ずつ走って山から正しい次の形を選び、空欄に置き、次の走者にタッチする。\n\n5つの数列（簡単→難しい）：\n1. 🔴🔵🔴 🔴🔵🔴 🔴🔵__ → 🔴\n2. 🟡🔵🔵 🟡🔵🔵 🟡🔵__ → 🔵\n3. 🟢🟢🔴 🟢🟢🔴 🟢🟢__ → 🔴\n4. 🔺🔺🟦🟦 🔺🔺🟦🟦 🔺🔺__ → 🟦\n5. AI用語（形の代わりに単語カードを使う）：「データ→___→予測」→パターン`,
          },
        },
        {
          time: '14:45 – 15:15',
          duration: { en: '30 min', ja: '30分' },
          title: { en: '5-Question Relay Quiz', ja: '5問リレークイズ' },
          type: 'game',
          emoji: '🏆',
          content: {
            en: `Same relay format as Day 1.\n\n5 QUESTIONS:\n1. What is training data? → Examples shown to AI to teach it\n2. True/False: More data makes AI more accurate → TRUE\n3. What does AI look for in data? → PATTERNS\n4. In the Human Robot Game, what did the robot do when confused? → Froze / had no rule\n5. Who decides what data AI learns from? → HUMANS`,
            ja: `Day1と同じリレー形式。\n\n5つの質問：\n1. 訓練データとは？→AIを教えるために見せる例\n2. 正誤：データが多いほどAIは正確になる→正\n3. AIはデータの中の何を探す？→パターン\n4. 人間ロボットゲームで混乱したときロボットはどうした？→フリーズした・ルールがなかった\n5. AIが学ぶデータを決めるのは？→人間`,
          },
        },
        {
          time: '15:15 – 16:00',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Gallery Walk + Wrap-Up', ja: 'ギャラリーウォーク＋振り返り' },
          type: 'share',
          emoji: '🔄',
          content: {
            en: `Groups walk around to see all 4 comics + creatures. Circle up: "What is one thing your creature learned today?"\n\nPreview: "Tomorrow: even a well-fed creature can still make mistakes. We find out why — and how to fix it!"`,
            ja: `グループは教室を回って全4体の漫画と相棒を見る。円になって：「今日相棒が学んだことを1つ教えて？」\n\n予告：「明日：しっかりエサをあげた相棒でもまだ間違えることがあります。なぜか——そしてどう直すか、探ってみましょう！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'ai2-learning',
        emoji: '🧠',
        title: { en: 'How My Creature Learns', ja: '相棒の学び方' },
        fields: [
          { type: 'header', label: { en: 'Data → Patterns → Predictions', ja: 'データ → パターン → 予測' } },
          { type: 'line', label: { en: 'Training data means:', ja: '訓練データとは：' } },
          { type: 'header', label: { en: 'Human Robot Game', ja: '人間ロボットゲーム' } },
          { type: 'multiline', label: { en: 'What happened when the robot saw something new?', ja: '新しいものを見たときロボットに何が起きた？' } },
          { type: 'header', label: { en: 'My Learning', ja: '今日の学び' } },
          { type: 'draw', label: { en: 'Draw the 3 steps: Data → Patterns → Predictions', ja: '3ステップを絵で描こう：データ→パターン→予測' } },
        ],
      },
    ],
  },

  // ─── DAY 3 ───────────────────────────────────────────────
  {
    day: 3,
    weekday: { en: 'Wednesday', ja: '水曜日' },
    theme: { en: 'AI Gets It Wrong — Glitch Stickers', ja: 'AIの間違い——グリッチステッカー' },
    goal: {
      en: 'Groups can name the 4 mistake types using color-coded stickers, stick "glitches" on their creature, and design a paper "patch" to fix each one.',
      ja: '色分けされたステッカーを使い4つのミスタイプを説明でき、相棒に「グリッチ」を貼り、それぞれを直す紙の「パッチ」をデザインできるようになる。',
    },
    vocab: [
      { word: { en: 'BIAS', ja: 'バイアス' }, def: { en: 'When AI treats some people unfairly because its training data favoured certain groups.', ja: '訓練データが特定のグループを優遇していたため、AIが一部の人を不公平に扱うこと' }, emoji: '⚖️' },
      { word: { en: 'HALLUCINATION', ja: 'ハルシネーション' }, def: { en: 'When AI makes up information that sounds true but is completely wrong.', ja: 'AIが本物らしく聞こえるが完全に間違っている情報を作り出すこと' }, emoji: '💭' },
      { word: { en: 'EDGE CASE', ja: 'エッジケース' }, def: { en: 'A situation AI was never trained for, so it fails or guesses badly.', ja: 'AIが訓練されたことのない状況——失敗するか悪い予測をする' }, emoji: '⚠️' },
      { word: { en: 'BAD DATA', ja: '悪いデータ' }, def: { en: 'Garbage in, garbage out — poor quality training data creates poor AI.', ja: '入れたものがゴミならゴミしか出ない——低品質な訓練データは低品質なAIを作る' }, emoji: '🗑️' },
      { word: { en: 'RED TEAMING', ja: 'レッドチーミング' }, def: { en: 'Testing AI by deliberately trying to find weaknesses before release.', ja: 'リリース前に意図的に弱点を探してAIをテストすること' }, emoji: '🔴' },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Recap — The Frozen Robot', ja: '振り返り——フリーズしたロボット' },
          type: 'lesson',
          emoji: '🔄',
          content: {
            en: `Teacher retells yesterday's Human Robot freeze moment. "Why did it freeze? Even a well-fed creature can still get confused — today we find out the 4 ways that happens."`,
            ja: `先生が昨日の人間ロボットがフリーズした瞬間を振り返る。「なぜフリーズした？しっかりエサをあげた相棒でも混乱することがあります——今日は4つの理由を見つけます。」`,
          },
        },
        {
          time: '10:45 – 11:30',
          duration: { en: '45 min', ja: '45分' },
          title: { en: '4 Glitch Types — Color Sticker Match', ja: '4つのグリッチタイプ——色ステッカーマッチング' },
          type: 'activity',
          emoji: '🃏',
          content: {
            en: `4 GLITCH TYPES, color-coded:\n🔴 Red = UNFAIR (learned from an unfair example)\n🟣 Purple = MADE IT UP (guessed something totally untrue)\n🟡 Yellow = NEVER SEEN THIS (a brand-new situation)\n🟢 Green = BAD EXAMPLE (taught with a wrong/messy example)\n\nTeacher reads 8 short, 1-sentence real mistake stories. After each, groups hold up the matching color sticker.\n\nEXAMPLES:\n"Face recognition works great on light skin, badly on dark skin." → 🔴 Red\n"A chatbot invented 6 court cases that don't exist." → 🟣 Purple\n"A self-driving car couldn't see a stop sign covered in snow." → 🟡 Yellow\n"A weather AI trained on European data is wrong for Okinawa." → 🟢 Green`,
            ja: `4つのグリッチタイプ、色分けされている：\n🔴 赤＝不公平（不公平な例から学んだ）\n🟣 紫＝作り話（完全に嘘のことを予測した）\n🟡 黄＝初めて見た（全く新しい状況）\n🟢 緑＝悪い例（間違った・ぐちゃぐちゃな例で教わった）\n\n先生が実際のミスの話を1文ずつ8つ読む。それぞれの後、グループが対応する色のステッカーを掲げる。\n\n例：\n「顔認識が白い肌ではうまくいくが、黒い肌ではうまくいかない。」→🔴赤\n「チャットボットが存在しない裁判例を6件作り出した。」→🟣紫\n「自動運転車が雪で覆われた一時停止標識を見えなかった。」→🟡黄\n「ヨーロッパのデータで訓練した天気AIが沖縄では間違える。」→🟢緑`,
          },
        },
        {
          time: '11:30 – 12:00',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Stick a Glitch on Your Creature', ja: '相棒にグリッチを貼ろう' },
          type: 'activity',
          emoji: '🔴',
          content: {
            en: `Each group picks 2 glitch stickers and sticks them on their creature, writing next to each: "My creature could make this mistake if ___."\n\n💡 STAFF TIP: Encourage silly, imaginative examples — the goal is understanding the category, not being 100% technically correct.`,
            ja: `各グループがグリッチステッカーを2枚選び相棒に貼る。それぞれの横に書く：「相棒はもし___なら、このミスをするかもしれません。」\n\n💡 スタッフへのアドバイス：面白くて創造的な例を歓迎しましょう——目的はカテゴリーを理解することで、技術的に100%正確である必要はありません。`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 14:15',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Patch It Challenge', ja: 'パッチを貼ろうチャレンジ' },
          type: 'activity',
          emoji: '🔧',
          content: {
            en: `For each glitch sticker on their creature, the group designs a paper "patch" — a colored A3 cutout with one fix written/drawn on it — and tapes it over the glitch.\n\nEXAMPLE: patch over 🔴 Unfair = "show it examples from ALL kinds of people."\n\nAfter all 5: "What do all these fixes have in common?" → BETTER DATA + HUMAN CHECKING.`,
            ja: `相棒に貼った各グリッチステッカーに対し、グループが紙の「パッチ」（色画用紙の切り抜きに修正案を書いたもの）をデザインし、グリッチの上に貼る。\n\n例：🔴不公平のパッチ＝「あらゆる種類の人の例を見せる」。\n\n全て終わったら：「これらの修正の共通点は？」→より良いデータ＋人間による確認`,
          },
        },
        {
          time: '14:15 – 14:45',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Group Swap — Spot the Glitch', ja: 'グループ交換——グリッチを見つけよう' },
          type: 'share',
          emoji: '🔍',
          content: {
            en: `Groups rotate to a neighboring creature and try to spot an un-patched glitch, or suggest a better patch. Light peer teaching, no pressure — every idea is welcome.`,
            ja: `グループが隣のグループの相棒のところへ移動し、まだパッチが貼られていないグリッチを探すか、より良いパッチを提案する。軽いお互いの学び合い——プレッシャーなし、どんなアイデアも歓迎。`,
          },
        },
        {
          time: '14:45 – 15:15',
          duration: { en: '30 min', ja: '30分' },
          title: { en: '5-Question Relay Quiz', ja: '5問リレークイズ' },
          type: 'game',
          emoji: '🏆',
          content: {
            en: `5 QUESTIONS:\n1. What color is UNFAIR (bias)? → RED\n2. What color is MADE IT UP (hallucination)? → PURPLE\n3. What is an edge case? → A situation AI was never trained for\n4. "Garbage in, garbage out" means ___ → Bad data makes bad AI\n5. Who is responsible when AI fails? → The humans who built and used it`,
            ja: `5つの質問：\n1. 不公平（バイアス）は何色？→赤\n2. 作り話（ハルシネーション）は何色？→紫\n3. エッジケースとは？→AIが訓練されたことのない状況\n4.「ゴミを入れればゴミが出る」とは？→悪いデータは悪いAIを作る\n5. AIが失敗したとき責任があるのは？→作って使った人間`,
          },
        },
        {
          time: '15:15 – 16:00',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Wrap-Up Circle', ja: '振り返りサークル' },
          type: 'share',
          emoji: '🔄',
          content: {
            en: `Drill the big one: "Whose fault is it when AI messes up?" → "The HUMANS who built and used it!"\n\nPreview: "Tomorrow: we go hunting for AI hiding all over the real world — and design a brand new one!"`,
            ja: `一番大事なことを繰り返す：「AIが間違えたら誰の責任？」→「作って使った人間！」\n\n予告：「明日：現実世界に隠れているAIを探しに行きます——そして新しいAIをデザインします！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'ai3-mistakes',
        emoji: '⚠️',
        title: { en: 'Glitch & Patch Sheet', ja: 'グリッチ＆パッチシート' },
        fields: [
          { type: 'header', label: { en: 'The 4 Glitch Colors', ja: '4つのグリッチカラー' } },
          { type: 'line', label: { en: '🔴 Red (Unfair) means:', ja: '🔴 赤（不公平）とは：' } },
          { type: 'line', label: { en: '🟣 Purple (Made It Up) means:', ja: '🟣 紫（作り話）とは：' } },
          { type: 'line', label: { en: '🟡 Yellow (Never Seen This) means:', ja: '🟡 黄（初めて見た）とは：' } },
          { type: 'line', label: { en: '🟢 Green (Bad Example) means:', ja: '🟢 緑（悪い例）とは：' } },
          { type: 'header', label: { en: 'Our Creature\'s Glitches', ja: '私たちの相棒のグリッチ' } },
          { type: 'multiline', label: { en: 'Glitch 1 and our patch:', ja: 'グリッチ1とパッチ：' } },
          { type: 'multiline', label: { en: 'Glitch 2 and our patch:', ja: 'グリッチ2とパッチ：' } },
        ],
      },
    ],
  },

  // ─── DAY 4 ───────────────────────────────────────────────
  {
    day: 4,
    weekday: { en: 'Thursday', ja: '木曜日' },
    theme: { en: 'AI in My World — Badge Hunt & Invention', ja: '生活の中のAI——バッジハント＆発明' },
    goal: {
      en: 'Groups collect real-world "AI badges" around the room, debate good vs. risky uses, and design their own new AI invention.',
      ja: 'グループが教室内の実生活「AIバッジ」を集め、良い使い方と危険な使い方を議論し、自分たちの新しいAI発明をデザインする。',
    },
    vocab: [
      { word: { en: 'COMPUTER VISION', ja: 'コンピュータビジョン' }, def: { en: 'AI that can see and understand images and video.', ja: '画像や動画を見て理解できるAI' }, emoji: '👁️' },
      { word: { en: 'NLP', ja: '自然言語処理' }, def: { en: 'Natural Language Processing — AI that understands and produces human words.', ja: '自然言語処理——人間の言葉を理解し生成するAI' }, emoji: '💬' },
      { word: { en: 'RECOMMENDATION', ja: 'レコメンデーション' }, def: { en: 'AI suggesting things based on your past choices — YouTube, Netflix, Spotify.', ja: '過去の選択に基づいてAIが提案すること——YouTube・Netflix・Spotify' }, emoji: '📱' },
      { word: { en: 'AI ETHICS', ja: 'AI倫理' }, def: { en: 'Rules for making AI that is fair, safe, and beneficial for everyone.', ja: '全ての人にとって公平・安全・有益なAIを作るためのルール' }, emoji: '⚖️' },
      { word: { en: 'AI LITERACY', ja: 'AIリテラシー' }, def: { en: 'Understanding what AI is, how it works, and how to use it wisely and responsibly.', ja: 'AIとは何か・どう機能するか・どう賢く責任を持って使うかを理解すること' }, emoji: '📖' },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Lightning Recap', ja: 'クイックリコール' },
          type: 'lesson',
          emoji: '⚡',
          content: {
            en: `Fast call-and-response of Days 1–3: "AI learns from ___" → DATA; "AI finds ___" → PATTERNS; "Name the 4 glitch colors" → Red/Purple/Yellow/Green.\n\nSAY THIS: "Your creature has learned, made mistakes, and been fixed. Now let's find where creatures like it already live in the real world."`,
            ja: `1〜3日目のクイックコール＆レスポンス：「AIは___から学ぶ」→データ；「AIは___を見つける」→パターン；「4つのグリッチカラーを言って」→赤・紫・黄・緑。\n\n先生が言うこと：「相棒は学び、間違え、直されました。今度は同じような相棒が現実世界のどこにいるか見つけましょう。」`,
          },
        },
        {
          time: '10:45 – 11:30',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Badge Hunt — 6 Scene Posters', ja: 'バッジハント——6枚のシーンポスター' },
          type: 'activity',
          emoji: '🔍',
          content: {
            en: `MATERIALS: 6 large A3 "scene posters" (hand-drawn is fine) taped around the room — Phone, Hospital, Car, School, Home, Farm — each with 3–4 small paper "AI badges" pinned to it.\n\nGroups rotate station to station (5 min each). At each station, they collect one badge by explaining out loud why it's AI, then stick the badge onto their creature.\n\nBy the end, every creature is covered in real-world AI badges.`,
            ja: `必要なもの：教室に貼る6枚の大きなA3「シーンポスター」（手描きでOK）——スマホ・病院・車・学校・家・農場——それぞれに3〜4枚の小さな紙「AIバッジ」がピン留めされている。\n\nグループは各ステーションを順番に回る（各5分）。各ステーションでバッジを1枚もらうには、なぜそれがAIか声に出して説明する。もらったバッジは相棒に貼る。\n\n最後には全ての相棒が現実世界のAIバッジで覆われる。`,
          },
        },
        {
          time: '11:30 – 12:00',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Good vs Risky — Walk the Line', ja: '良い vs 危険——ラインを歩こう' },
          type: 'game',
          emoji: '⚖️',
          content: {
            en: `Tape a line on the floor: one end "👍 Good use," other end "⚠️ Needs care." Teacher reads a scenario; whole class physically stands where they feel on the line. 2–3 students explain.\n\n6 SCENARIOS:\n1. AI that helps doctors spot illness earlier.\n2. AI that watches students' faces to check attention.\n3. AI that lets people who can't speak communicate with their eyes.\n4. AI that decides who goes to prison based on past data.\n5. AI that translates speech in real time.\n6. AI that scans public faces to find missing children.\n\nAfter all 6: "What makes the difference between good and risky AI?" → HUMAN OVERSIGHT + FAIRNESS.`,
            ja: `床にテープでラインを貼る：片方の端「👍良い使い方」、もう片方「⚠️注意が必要」。先生がシナリオを読む。クラス全員が自分の立場に物理的に立つ。2〜3人が理由を説明する。\n\n6つのシナリオ：\n1. 病気を早期発見する医師を助けるAI。\n2. 生徒の顔を監視して集中度を確認するAI。\n3. 話せない人が目でコミュニケーションできるAI。\n4. 過去のデータから誰を刑務所に入れるか決めるAI。\n5. リアルタイムで音声翻訳するAI。\n6. 行方不明の子どもを探すため公共の顔をスキャンするAI。\n\n全6件後：「良いAIと危険なAIの違いは？」→人間の監視＋公平性`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 14:30',
          duration: { en: '60 min', ja: '60分' },
          title: { en: 'Invent a New Creature', ja: '新しい相棒を発明しよう' },
          type: 'activity',
          emoji: '🎨',
          content: {
            en: `On a fresh A3 sheet, each group designs a brand-new AI helper using a simple 4-box template drawn at the top of the sheet:\n① Problem it solves\n② What it does\n③ What it needs to learn from\n④ Who might it accidentally leave out\n\nGroups may build a small physical prop for it out of scrap paper/box lids if time allows.\n\n4 TOPIC IDEAS if a group is stuck: Healthcare, Environment, Education, Safety.`,
            ja: `新しいA3用紙に、各グループが用紙上部の簡単な4マステンプレートを使って新しいAIヘルパーをデザインする：\n①解決する問題\n②実際に何をするか\n③何から学ぶ必要があるか\n④誰が取り残されるかもしれないか\n\n時間があれば余った紙や箱のフタで小さな模型を作ってもよい。\n\n困ったグループへの4つのテーマ案：ヘルスケア・環境・教育・安全`,
          },
        },
        {
          time: '14:30 – 15:15',
          duration: { en: '45 min', ja: '45分' },
          title: { en: '30-Second Pitch', ja: '30秒ピッチ' },
          type: 'share',
          emoji: '🎤',
          content: {
            en: `Each group presents their invention to the other 3 groups in 30 seconds: "Our AI is called ___. It solves ___. It learns from ___."\n\nAudience gives 1 of 3 paper reaction cards: 👍 Love it / 🤔 Needs work / ❓ Risky — think again.`,
            ja: `各グループが他の3グループに30秒で発明を発表する：「私たちのAIの名前は___。___を解決します。___から学びます。」\n\n観客は3種類の紙のリアクションカードから1枚渡す：👍最高！／🤔もっと工夫が必要／❓危険——考え直そう`,
          },
        },
        {
          time: '15:15 – 16:00',
          duration: { en: '45 min', ja: '45分' },
          title: { en: '5-Question Relay Quiz + Wrap-Up', ja: '5問リレークイズ＋振り返り' },
          type: 'game',
          emoji: '🏆',
          content: {
            en: `5 QUESTIONS:\n1. Name 2 places AI is used in real life → any 2 valid\n2. What is a recommendation system? → AI that suggests things based on your past choices\n3. Name ONE good use of AI → any valid example\n4. Name ONE risky use of AI → any valid example\n5. Complete: "AI is a ___ — humans are ___" → TOOL — RESPONSIBLE\n\nPreview: "Tomorrow is graduation! Museum walk, AI Court, and certificates."`,
            ja: `5つの質問：\n1. AIが使われている場所を2つ→任意2つ\n2. レコメンデーションシステムとは？→過去の選択に基づいて提案するAI\n3. 良いAIの使い方を1つ→任意の有効な例\n4. 危険なAIの使い方を1つ→任意の有効な例\n5. 完成：「AIは___——人間は___」→ツール——責任者\n\n予告：「明日は卒業式！ミュージアムウォーク、AI法廷、そして修了証があります。」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'ai4-design',
        emoji: '🎨',
        title: { en: 'My New AI Invention', ja: '私の新しいAI発明' },
        fields: [
          { type: 'line', label: { en: 'AI Name:', ja: 'AI名：' } },
          { type: 'multiline', label: { en: '🚨 Problem it solves:', ja: '🚨 解決する問題：' } },
          { type: 'multiline', label: { en: '⚙️ What it does:', ja: '⚙️ 実際にすること：' } },
          { type: 'multiline', label: { en: '📊 What it learns from:', ja: '📊 何から学ぶか：' } },
          { type: 'multiline', label: { en: '👥 Who might it leave out:', ja: '👥 誰が取り残されるかも：' } },
          { type: 'draw', label: { en: 'Draw your AI:', ja: 'あなたのAIを描こう：' } },
        ],
      },
    ],
  },

  // ─── DAY 5 ───────────────────────────────────────────────
  {
    day: 5,
    weekday: { en: 'Friday', ja: '金曜日' },
    theme: { en: 'Graduation Day! — Museum Walk & Ceremony', ja: '卒業の日！——ミュージアムウォーク＆式典' },
    goal: {
      en: 'Groups showcase their finished creature in a museum walk, play AI Court, and graduate as proud AI Literacy Bootcamp graduates.',
      ja: 'グループが完成した相棒をミュージアムウォークで発表し、AI法廷ゲームを楽しみ、誇らしいAIリテラシーブートキャンプ卒業生として卒業する。',
    },
    vocab: [
      { word: { en: 'RESPONSIBLE', ja: '責任' }, def: { en: 'Taking ownership of your choices and their consequences — especially with AI.', ja: '自分の選択とその結果に責任を持つこと——特にAIに関して' }, emoji: '🤝' },
      { word: { en: 'DIVERSE', ja: '多様' }, def: { en: 'Including many different types of people, ideas, and data — key to fair AI.', ja: '様々な種類の人・アイデア・データを含む——公平なAIに不可欠' }, emoji: '🌍' },
      { word: { en: 'OVERSIGHT', ja: '監視・監督' }, def: { en: 'Humans checking and supervising AI to make sure it works fairly and correctly.', ja: '人間がAIを確認・監督して公平かつ正確に機能することを確認する' }, emoji: '👀' },
      { word: { en: 'FAIR', ja: '公平' }, def: { en: 'AI that works equally well for all people, regardless of who they are.', ja: '誰であるかに関わらず、全ての人に等しく機能するAI' }, emoji: '⚖️' },
      { word: { en: 'DESIGN', ja: 'デザイン' }, def: { en: 'Planning and creating something with purpose — AI designers shape how AI affects the world.', ja: '目的を持って計画し作ること——AIデザイナーがAIが世界に与える影響を形作る' }, emoji: '✏️' },
    ],
    morning: {
      time: { en: 'Morning Session  10:30 – 12:00', ja: '午前の部  10:30〜12:00' },
      activities: [
        {
          time: '10:30 – 10:45',
          duration: { en: '15 min', ja: '15分' },
          title: { en: 'Read Back Day 1', ja: '1日目を読み返そう' },
          type: 'share',
          emoji: '📜',
          content: {
            en: `Read back each group's Day 1 sentence card ("AI is a machine that ___"). Ask if anyone wants to update it now that the week is over — compare old vs new side by side.`,
            ja: `各グループの1日目の文カード（「AIは___する機械です」）を読み返す。1週間終わった今、更新したい人がいるか聞く——古い文と新しい文を並べて比較する。`,
          },
        },
        {
          time: '10:45 – 11:30',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'Final Touch-Up', ja: '最後の仕上げ' },
          type: 'activity',
          emoji: '🖌️',
          content: {
            en: `Groups get 30 minutes to add any last decorations, finish patches/badges, and prepare a 1-minute "tour" of their creature: what it ate, what mistakes it made, how it got fixed, what it can do now.`,
            ja: `グループは30分で最後の仕上げをし、パッチやバッジを完成させ、相棒の1分間「ツアー」を準備する：何を食べたか・どんなミスをしたか・どう直したか・今何ができるか。`,
          },
        },
        {
          time: '11:30 – 12:00',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'Museum Walk', ja: 'ミュージアムウォーク' },
          type: 'share',
          emoji: '🏛️',
          content: {
            en: `Half of each group stays with their creature as "tour guides," the other half visits the other 3 creatures — then swap. Visiting groups leave one compliment sticky note per creature.`,
            ja: `各グループの半分が相棒の前に残り「ツアーガイド」役になる。残りの半分が他3体の相棒を見に行く——その後交代。訪問したグループは各相棒に感想の付箋を1枚残す。`,
          },
        },
      ],
    },
    afternoon: {
      time: { en: 'Afternoon Session  13:30 – 16:00', ja: '午後の部  13:30〜16:00' },
      activities: [
        {
          time: '13:30 – 14:15',
          duration: { en: '45 min', ja: '45分' },
          title: { en: 'AI Court — Fun Mock Trial', ja: 'AI法廷——楽しい模擬裁判' },
          type: 'game',
          emoji: '⚖️',
          content: {
            en: `Teacher reads a short, silly "AI on trial" scenario (e.g., "This AI recommended pineapple pizza to everyone!"). Two groups argue "defend the AI" vs. "the humans should have tested it better," other two groups act as jury and vote. Rotate roles for a second scenario.\n\nAll in plain, playful language — no real legal knowledge needed.`,
            ja: `先生が短くて面白い「AI裁判」シナリオを読む（例：「このAIは全員にパイナップルピザをおすすめした！」）。2グループが「AIを弁護する」vs「人間がもっとテストすべきだった」で議論し、残り2グループが陪審員役で投票する。2つ目のシナリオで役割を交代。\n\n全て分かりやすく楽しい言葉で——本物の法律知識は不要。`,
          },
        },
        {
          time: '14:15 – 14:45',
          duration: { en: '30 min', ja: '30分' },
          title: { en: 'One-Word Memory Circle', ja: 'ワンワードメモリーサークル' },
          type: 'share',
          emoji: '💬',
          content: {
            en: `Every student says one word they'll remember from the week — no repeats! If the word is taken, use it in a silly sentence instead.`,
            ja: `全員が今週覚えておきたい単語を1つ言う——繰り返しなし！もし単語が被ったら、その単語を使った面白い文を言う。`,
          },
        },
        {
          time: '14:45 – 16:00',
          duration: { en: '75 min', ja: '75分' },
          title: { en: 'Graduation Ceremony', ja: '卒業式' },
          type: 'stamp',
          emoji: '🎓',
          content: {
            en: `STEP 1 — COUNT YOUR STAMPS (10 min): Students count all stamps in booklet, write total on graduation page. Teacher signs the certificate page in each book.\n\nSTEP 2 — CALL-AND-RESPONSE FINALE (10 min):\n• "What is AI?" → "A machine that learns from DATA!"\n• "What can go wrong?" → "Bias! Hallucination! Edge cases! Bad data!"\n• "Who is responsible?" → "The HUMANS!"\n• "Are you afraid of AI?" → "No — we UNDERSTAND it!"\n\nSTEP 3 — CERTIFICATES + GROUP PHOTO (30 min): Certificates handed out per student. Group photo with all 4 creatures.\n\nSTEP 4 — GROUP CELEBRATION (5 min): Class stands in a circle and shouts: "AI LITERACY — COMPLETE! AIリテラシー — コンプリート！"`,
            ja: `ステップ1——スタンプを数えよう（10分）：生徒がブックレットのスタンプを全部数え、卒業ページに合計を書く。先生が各ブックの修了証ページにサインする。\n\nステップ2——コール＆レスポンスフィナーレ（10分）：\n・「AIとは？」→「データから学ぶ機械！」\n・「何が問題になりうる？」→「バイアス！ハルシネーション！エッジケース！悪いデータ！」\n・「責任があるのは？」→「人間！」\n・「AIが怖い？」→「怖くない——理解しているから！」\n\nステップ3——修了証＋集合写真（30分）：各生徒に修了証を授与。全4体の相棒と集合写真。\n\nステップ4——グループお祝い（5分）：クラスが円になって叫ぶ：「AIリテラシー——コンプリート！AI LITERACY — COMPLETE！」`,
          },
        },
      ],
    },
    worksheets: [
      {
        id: 'ai5-graduation',
        emoji: '🎓',
        title: { en: 'AI Literacy Bootcamp — My Graduation Page', ja: 'AIリテラシーブートキャンプ——私の卒業ページ' },
        fields: [
          { type: 'header', label: { en: 'My Week in Review', ja: '今週の振り返り' } },
          { type: 'line', label: { en: 'Total stamps this week:', ja: '今週のスタンプ合計：' } },
          { type: 'multiline', label: { en: 'My creature\'s name and what it can do now:', ja: '相棒の名前と今できること：' } },
          { type: 'header', label: { en: 'The 3 Steps of AI', ja: 'AIの3ステップ' } },
          { type: 'line', label: { en: 'Step 1:', ja: 'ステップ1：' } },
          { type: 'line', label: { en: 'Step 2:', ja: 'ステップ2：' } },
          { type: 'line', label: { en: 'Step 3:', ja: 'ステップ3：' } },
          { type: 'header', label: { en: 'My Promise', ja: '私の約束' } },
          { type: 'multiline', label: { en: '"I will use AI responsibly by…"', ja: '「私はこのようにAIを責任を持って使います…」' } },
        ],
      },
    ],
  },
];
